import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "@/i18n/IntlProvider";
import { api } from "@/lib/api";

type RecentScanResponse = {
  occurredAt: string;
  userAgent: string | null;
  referer: string | null;
};

type ScanRecentResponse = {
  scans: RecentScanResponse[];
};

/**
 * Recent-scans table — top N rows from the outbox in reverse-chrono
 * order. Only meaningful for DYNAMIC codes (STATIC ones have no
 * redirect, the matrix is read straight off the phone, no event row).
 *
 * The backend already strips the hashed IP from the response so the
 * browser receives only `occurredAt + userAgent + referer`. We further
 * boil down the UA to a short label (`Chrome`, `Safari`, …) so the
 * cell stays readable; the full string lives in the row's `title`
 * attribute for the curious.
 */
export function RecentScansCard({
  codeId,
  mode,
  limit = 10,
}: {
  codeId: string;
  mode: "STATIC" | "DYNAMIC";
  limit?: number;
}) {
  const t = useTranslations("app.codes.detail.scans");

  const query = useQuery({
    queryKey: ["code", codeId, "scans", "recent", limit],
    queryFn: () =>
      api<ScanRecentResponse>(`/v1/codes/${codeId}/scans/recent?limit=${limit}`),
    enabled: mode === "DYNAMIC",
    retry: false,
    staleTime: 30_000,
  });

  if (mode !== "DYNAMIC") return null;

  const scans = query.data?.scans ?? [];

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <h2 className="text-sm font-semibold">{t("recentTitle")}</h2>
      {query.isLoading ? (
        <p className="text-xs text-muted-foreground">{t("loading")}</p>
      ) : scans.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("recentEmpty")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th
                  className="cursor-help px-2 py-1.5 font-medium underline decoration-dotted decoration-muted-foreground/40 underline-offset-[3px]"
                  title={t("recentColTimeTooltip")}
                >
                  {t("recentColTime")}
                </th>
                <th className="px-2 py-1.5 font-medium">{t("recentColAgent")}</th>
                <th className="px-2 py-1.5 font-medium">
                  {t("recentColReferer")}
                </th>
              </tr>
            </thead>
            <tbody>
              {scans.map((scan, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-2 py-1.5 tabular-nums">
                    {formatTime(scan.occurredAt)}
                  </td>
                  <td
                    className="px-2 py-1.5"
                    title={scan.userAgent ?? ""}
                  >
                    {labelForUserAgent(scan.userAgent)}
                  </td>
                  <td
                    className="px-2 py-1.5 text-muted-foreground"
                    title={scan.referer ?? ""}
                  >
                    {scan.referer ? hostOf(scan.referer) : t("recentDirect")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Includes seconds — the recent-scans feed is the only place we surface
  // exact event times to the user (the chart aggregates by day), so dropping
  // seconds makes two scans that happened within the same minute look
  // identical. The label is formatted in the browser's local zone via
  // `toLocaleString(undefined, …)`; the column header carries a tooltip
  // explaining that the wire format is UTC.
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Tiny browser-name heuristic — full UA-parsing libraries weigh in the
 * dozens of kB and aren't worth it for a recent-scans cell. The full
 * UA string stays in the row's `title` attribute for power users.
 */
function labelForUserAgent(ua: string | null): string {
  if (!ua) return "—";
  if (/Edg\//.test(ua)) return "Edge";
  if (/OPR\/|Opera/.test(ua)) return "Opera";
  if (/Chrome\/[0-9]/.test(ua) && !/Edg\//.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua) && /Version\//.test(ua)) return "Safari";
  if (/curl/i.test(ua)) return "curl";
  if (/bot|crawl|spider/i.test(ua)) return "Bot";
  return "Other";
}

function hostOf(url: string): string {
  try {
    return new URL(url).host || url;
  } catch {
    return url;
  }
}
